import React, { useEffect, useState, useCallback } from "react";
import { Flame, Phone, MessageSquarePlus, ArrowDownWideNarrow } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PredictionBadge, StatusBadge } from "@/components/badges";
import VoterEditDialog from "@/components/VoterEditDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const hColor = (h) => (h >= 70 ? "#10B981" : h >= 40 ? "#F59E0B" : "#EF4444");

export default function Priorities() {
  const { committee, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [editId, setEditId] = useState(null);

  useEffect(() => { api.get("/meta").then(({ data }) => setTeam(data.team_members)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: 60 };
    if (user.role === "super_admin" && committee !== "all") params.committee_no = committee;
    try {
      const { data } = await api.get("/priorities", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [committee, user.role]);

  useEffect(() => { if (committee !== null) load(); }, [load, committee]);

  return (
    <div className="space-y-4 max-w-4xl" data-testid="priorities-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
          <Flame className="h-6 w-6 text-orange-500" />Akıllı Öncelik Listesi
        </h1>
        <p className="text-sm text-muted-foreground">
          Sağlık skoru düşük komitelerdeki, henüz aranmamış "Belki" (kararsız) FAAL firmalar — önce bunları arayın.
        </p>
      </div>

      {loading || !data ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : data.items.length === 0 ? (
        <Card className="p-12 border border-border text-center text-muted-foreground">Harika! Aranmamış "Belki" firma kalmamış.</Card>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ArrowDownWideNarrow className="h-4 w-4" />
            {data.total} öncelikli firma — en riskli komiteler en üstte
          </div>
          <div className="space-y-2">
            {data.items.map((v, i) => (
              <Card key={v.id} className="p-4 border border-border flex items-center gap-3" data-testid={`priority-${v.id}`}>
                <div className="h-8 w-8 rounded-full bg-orange-100 text-orange-600 grid place-items-center font-bold text-sm shrink-0">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{v.company_name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <StatusBadge value={v.status} />
                    <PredictionBadge value={v.current_vote_prediction_2026} />
                    <span className="text-xs text-muted-foreground">{v.branch} · {v.team_assignment_2026 || "-"}</span>
                    <Badge variant="outline" className="text-[10px]" style={{ color: hColor(v.committee_health), borderColor: hColor(v.committee_health) }}>
                      #{v.committee_no} sağlık {v.committee_health}
                    </Badge>
                  </div>
                </div>
                <Button asChild size="icon" className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid={`priority-call-${v.id}`}>
                  <a href={`tel:${(v.phone || "").replace(/\s/g, "")}`}><Phone className="h-4 w-4" /></a>
                </Button>
                <Button variant="outline" size="sm" className="shrink-0" onClick={() => setEditId(v.id)} data-testid={`priority-note-${v.id}`}>
                  <MessageSquarePlus className="h-4 w-4 mr-1" />Not
                </Button>
              </Card>
            ))}
          </div>
        </>
      )}

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => load()} team={team} />
    </div>
  );
}
